import { CopyOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Card, Flex, Typography, message } from "antd";
import React, { useCallback } from "react";
import { getNotesType } from "../../domain/notes.domain";
import useCheckDemoPath from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { getNotesDetails, handleAVSEmailGenerate } from "../../service/notes.service";
import { setEmailContent } from "../../slices/notesSlice";
import { EventType, trackEvent } from "../../utils/analytics";
import styles from "./notes.module.scss";

const POLLING_INTERVAL_AVS_EMAIL = 5000; // 5 seconds
const MAX_POLLING_TIME = 120000; // 2 minutes timeout

const EmailView = () => {
  const dispatch = useAppDispatch();
  const componentName = "notes";
  const checkDemoPath = useCheckDemoPath();

  const [isGenerating, setIsGenerating] = React.useState(false);
  const [showGenerateButton, setShowGenerateButton] = React.useState(false);
  const pollingStartTime = React.useRef<number | null>(null);

  const { isDemoPath } = useAppSelector((state) => state.appState);
  const { emailContent, notesDetails } = useAppSelector((state) => state.notes);

  // Poll for email completion
  const pollForEmail = useCallback(async () => {
    if (!notesDetails?.id) return;

    try {
      const response = await getNotesDetails(notesDetails.id);
      const data = response.data;

      if (data.avs_email) {
        dispatch(setEmailContent(data.avs_email));
        setIsGenerating(false);
        pollingStartTime.current = null;
        return;
      }

      // Check timeout
      if (pollingStartTime.current && Date.now() - pollingStartTime.current > MAX_POLLING_TIME) {
        message.error("Email generation timed out. Please try again.");
        setIsGenerating(false);
        setShowGenerateButton(true);
        pollingStartTime.current = null;
        return;
      }

      // Continue polling
      setTimeout(pollForEmail, POLLING_INTERVAL_AVS_EMAIL);
    } catch (error) {
      console.error("Error polling for email:", error);
      setIsGenerating(false);
      setShowGenerateButton(true);
    }
  }, [notesDetails?.id, dispatch]);

  // Initialize state
  React.useEffect(() => {
    if (isDemoPath && notesDetails?.avs_email) {
      dispatch(setEmailContent(notesDetails.avs_email));
    } else if (notesDetails?.avs_email) {
      dispatch(setEmailContent(notesDetails.avs_email));
    } else {
      setShowGenerateButton(true);
    }
  }, [notesDetails, isDemoPath, dispatch]);

  const handleGenerateEmail = async () => {
    if (checkDemoPath()) return;
    if (!notesDetails) return;

    trackEvent(EventType.GENERATE_CLIENT_EMAIL, {
      recordingId: notesDetails.id,
      noteType: getNotesType(notesDetails.note_type),
    });

    setShowGenerateButton(false);
    setIsGenerating(true);
    pollingStartTime.current = Date.now();

    try {
      const result = await handleAVSEmailGenerate(notesDetails.id);

      if (result.status === 200 && result.data.email) {
        // Already existed
        dispatch(setEmailContent(result.data.email));
        setIsGenerating(false);
      } else if (result.status === 202) {
        // Task enqueued, start polling
        setTimeout(pollForEmail, POLLING_INTERVAL_AVS_EMAIL);
      }
    } catch (error) {
      console.error(error);
      message.error("Error generating email. Please try again.");
      setIsGenerating(false);
      setShowGenerateButton(true);
    }
  };

  const handleCopyEmail = () => {
    if (notesDetails && emailContent) {
      trackEvent(EventType.COPY_CLIENT_EMAIL, {
        recordingId: notesDetails.id,
        noteType: getNotesType(notesDetails.note_type),
      });
      navigator.clipboard.writeText(emailContent);
      message.success("Email content copied to clipboard");
    }
  };

  if (!notesDetails) return null;

  return (
    <Flex vertical gap={20}>
      <Card
        hoverable
        title="Client Email"
        className={`${styles[`${componentName}__main-section`]}`}
        style={{ width: "100%" }}
      >
        {showGenerateButton ? (
          <Flex vertical gap={10}>
            <Typography>Would you like to generate an email summary for your client?</Typography>
            <Flex>
              <Button type="primary" onClick={handleGenerateEmail}>
                Generate Email
              </Button>
            </Flex>
          </Flex>
        ) : isGenerating ? (
          <Flex align="center">
            <LoadingOutlined />
            <Typography style={{ marginLeft: "10px", fontWeight: "bold", color: "#606060" }}>
              Your Nudge assistant is generating the client email
            </Typography>
          </Flex>
        ) : emailContent ? (
          <Flex vertical gap={10}>
            <Flex justify="flex-end">
              <Button icon={<CopyOutlined />} onClick={handleCopyEmail}>
                Copy Email
              </Button>
            </Flex>
            <div style={{ whiteSpace: "pre-wrap" }}>{emailContent}</div>
          </Flex>
        ) : (
          <Typography>No email content available.</Typography>
        )}
      </Card>
    </Flex>
  );
};

export default EmailView;
